import useChatStore, { type MemberChatJumpSource } from '@/stores/useChatStore';
import type { NavigateOptions } from '@tanstack/react-router';
import {
  resolveUserMentionsInCommentText,
  type MentionUser,
} from './data-transformers/comment';
import { CHAT_MESSAGE_HIGHLIGHT_MS } from '@/config/constants';

/** Session key used to scroll/highlight a chat message after navigation. */
export const SCROLL_TO_CHAT_MESSAGE_STORAGE_KEY = 'mani:scrollToChatMessageId';

export { CHAT_MESSAGE_HIGHLIGHT_MS };

/**
 * DOM id for a member chat message row (`message-{id}`).
 */
export function getChatMessageElementId(messageId: string | number): string {
  return `message-${String(messageId)}`;
}

function findScrollableChatContainer(element: HTMLElement): HTMLElement | null {
  let current: HTMLElement | null = element.parentElement;
  while (current) {
    if (current.id === 'member-chat-container' || current.id === 'ai-chat-container') {
      return current;
    }
    const style = window.getComputedStyle(current);
    const isScrollable =
      (style.overflowY === 'auto' || style.overflowY === 'scroll') &&
      current.scrollHeight > current.clientHeight;
    if (isScrollable) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
}

/**
 * Scrolls a message element into view within the chat container when possible.
 */
export function scrollChatMessageElementIntoView(element: HTMLElement): void {
  const container = findScrollableChatContainer(element);
  if (container) {
    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const elementTop = elementRect.top - containerRect.top + container.scrollTop;
    const containerHeight = container.clientHeight;
    const desiredScrollTop = elementTop - containerHeight / 2 + elementRect.height / 2;
    container.scrollTo({ top: desiredScrollTop, behavior: 'smooth' });
    return;
  }

  element.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

/**
 * Scrolls to a chat message by DOM id and applies the React-driven highlight pulse.
 */
export function scrollAndHighlightChatMessage(messageId: string | number): boolean {
  const element = document.getElementById(getChatMessageElementId(messageId));

  if (!element) {
    return false;
  }

  useChatStore.getState().requestMemberChatJumpToMessage(messageId, 'live');
  scrollChatMessageElementIntoView(element);
  window.setTimeout(() => {
    useChatStore.getState().clearMemberChatJumpToMessage();
  }, CHAT_MESSAGE_HIGHLIGHT_MS);

  return true;
}

/**
 * Truncates message text for reply preview display.
 */
export function truncateChatMessagePreview(text: string, maxLength = 120): string {
  const normalized = text.replace(/\s+/g, ' ').trim();
  if (normalized.length <= maxLength) {
    return normalized;
  }
  return `${normalized.slice(0, maxLength)}...`;
}

export function persistScrollToChatMessageId(messageId: string | number): void {
  sessionStorage.setItem(SCROLL_TO_CHAT_MESSAGE_STORAGE_KEY, String(messageId));
}

export function consumeScrollToChatMessageId(): string | null {
  const value = sessionStorage.getItem(SCROLL_TO_CHAT_MESSAGE_STORAGE_KEY);
  if (value) {
    sessionStorage.removeItem(SCROLL_TO_CHAT_MESSAGE_STORAGE_KEY);
  }
  return value;
}

type NavigateFn = (options: NavigateOptions) => void;

export type GroupChatMessageNavigationTarget = {
  groupId: string;
  messageId: string | number;
  folderId?: string;
  /** When known, whether the target lives in the live feed or vault. */
  source?: MemberChatJumpSource;
};

/**
 * Opens member chat, navigates to the group (or folder), and scrolls to the message once loaded.
 */
export function queueMemberChatMessageScroll(
  messageId: string | number,
  source?: MemberChatJumpSource,
): void {
  const normalizedId = String(messageId);
  persistScrollToChatMessageId(normalizedId);
  useChatStore.getState().requestMemberChatJumpToMessage(normalizedId, source);
}

export function navigateToGroupChatMessage(
  { groupId, messageId, folderId, source }: GroupChatMessageNavigationTarget,
  navigate: NavigateFn,
): void {
  queueMemberChatMessageScroll(messageId, source);
  useChatStore.getState().setMemberChatOpen(true);

  if (folderId) {
    navigate({
      to: '/group/$groupId/item/$folderId',
      params: { groupId, folderId },
    });
    return;
  }

  navigate({
    to: '/group/$groupId',
    params: { groupId },
  });
}

/**
 * Retries scrolling until the message element exists in the DOM.
 */
export function scrollToChatMessageWhenReady(
  messageId: string,
  onSuccess?: () => void,
): (() => void) | undefined {
  const attemptScroll = () => {
    const element = document.getElementById(getChatMessageElementId(messageId));
    if (!element) {
      return false;
    }

    useChatStore.getState().requestMemberChatJumpToMessage(messageId);
    scrollChatMessageElementIntoView(element);
    onSuccess?.();
    window.setTimeout(() => {
      useChatStore.getState().clearMemberChatJumpToMessage();
    }, CHAT_MESSAGE_HIGHLIGHT_MS);
    return true;
  };

  if (attemptScroll()) {
    return undefined;
  }

  let attempts = 0;
  const intervalId = window.setInterval(() => {
    attempts += 1;
    if (attemptScroll() || attempts >= 25) {
      window.clearInterval(intervalId);
    }
  }, 200);

  return () => window.clearInterval(intervalId);
}

/**
 * Resolves mention tokens then truncates for reply/parent message previews.
 */
export function formatChatMessagePreview(
  text: string,
  taggedUsers?: MentionUser[],
  maxLength = 120,
): string {
  const resolved = resolveUserMentionsInCommentText(text, taggedUsers);
  return truncateChatMessagePreview(resolved, maxLength);
}
